import "./interfaces.d.ts";
import "./API_interfaces.d.ts";

import { userInfoStore } from "Stores/UserInfoStore";
import { FetchError } from "General/ErrorTypes";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { API_URL } from "./APIEndpoints";
import Axios, { AxiosRequestConfig, AxiosError, ResponseType } from "axios";
import { axios } from "./axios";
import i18n, { I18N_NS } from "Config/i18n";
import { ssoLoginStore } from "Pages/Login/Stores/SSOLoginStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { getUrlParams } from "utils/getUrlParams";

/**
 * Available HTTP Header Keywords
 */
type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
// type FetchType = 'json' | 'blob' | 'text';
type BodyType = Object | string;

interface CreateRequestOpts extends Omit<AxiosRequestConfig, "url" | "data" | "method"> {
  method: Method;
  body?: Object;
  responseType?: ResponseType;
  signal?: AbortSignal;
  /*
   * If true, no authentication headers will be added to the request
   */
  withoutAuth?: boolean;
  /**
   * This is similar to a typical onDownloadProgress handler, but instead of reflecting
   * the progress of reading a file download, it is useful for backend requests that
   * take a long time to prepare data on the server side.
   * It also avoid GatewayTimeout errors. (also see PAVE-4516)
   * The server streams progress percentages line by line and then the final (JSON-) data
   * separated by a double newline ("\n\n").
   * To use it, ask BE colleage to use the LongRunner class there
   */
  onProgress?: (percent: number) => void;
}

export interface RequestMethodOpts extends Omit<CreateRequestOpts, "method" | "body"> {
  withoutAuth?: boolean;
}

type DeleteRequestMethodOpts = Omit<CreateRequestOpts, "method">;

// when url has "undefined" in query/parameters. Happens on fast clicking on sidebar links.
const UNDEFINED_IN_URL = "UNDEFINED_IN_URL";
// for cancelled requests
const ABORTED = "ABORTED";
// let urlReturned401Counter = 0;
export class API {
  public static logout() {
    return API.request(API_URL.LOGIN.logoutUser(), { method: "DELETE" });
  }

  /**
   * Deletes the Resource on the Server
   *
   * Uses Rest Convention of HTTP Method "DELETE"
   *
   * @param url
   */
  public static async deleteData(url: string, params?: Object, opts: DeleteRequestMethodOpts = {}) {
    const encodedUrl = new URL(url, `${window.location.origin}/${window.GLOBAL.API_BASE}/`);
    for (const keyName in params) {
      if (params[keyName]) {
        encodedUrl.searchParams.append(keyName, params[keyName]);
      }
    }

    return await this.request(encodedUrl.toString(), { ...opts, method: "DELETE" });
  }

  public static async editData<T>(url: string, body: Object, params?: Object, opts: RequestMethodOpts = {}) {
    return await this.request<T>(url, { method: "PUT", params, body, ...opts });
  }

  public static async patchData<T>(url: string, body: Object, params?: Object, opts: RequestMethodOpts = {}) {
    return await this.request<T>(url, { method: "PATCH", params, body, ...opts });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static async newData<T = any>(
    url: string,
    body: BodyType,
    params?: Object,
    opts: RequestMethodOpts = {},
    silent?: boolean
  ) {
    return await this.request<T>(
      url,
      {
        method: "POST",
        params,
        body,
        ...opts,
      },
      silent
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static async getData<T = any>(url: string, params?: Object, opts: RequestMethodOpts = {}) {
    return await this.request<T>(url, {
      method: "GET",
      params,
      responseType: "json",
      ...opts,
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static async getBlob(url: string, params?: Object, opts: RequestMethodOpts = {}) {
    return await this.request<File>(url, {
      method: "GET",
      params,
      responseType: "blob",
      ...opts,
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static async getBlobByPost(url: string, body: BodyType, params?: Object, opts: RequestMethodOpts = {}) {
    return await this.request<File>(url, {
      method: "POST",
      body,
      params,
      responseType: "blob",
      ...opts,
    });
  }

  public static async request<T = any>( // eslint-disable-line @typescript-eslint/no-explicit-any
    url: string,
    { body, onDownloadProgress, onProgress, ...opts }: CreateRequestOpts,
    silent: boolean = false
  ): Promise<T> {
    const isUndefined = url.indexOf("undefined") !== -1;
    if (isUndefined) {
      console.warn('Will not fetch url because of "undefined": ', url);
      throw UNDEFINED_IN_URL;
    }
    const requestConfig: AxiosRequestConfig = {
      ...opts,
      url,
      data: body,
      headers: opts.withoutAuth ? {} : this.headersObject(opts.headers),
    };
    if (onDownloadProgress) {
      requestConfig.onDownloadProgress = (evt) => {
        // PAVE-3018
        // back-end can not send normal content-length header, so we have to do magic tricks here
        if (!evt.total) {
          evt.total = +evt.event.srcElement.getResponseHeader("x-content-length");
          evt.progress = (evt.loaded / evt.total) * 100;
        }
        return onDownloadProgress(evt);
      };
    }
    let retryAfterTimeout = false;
    do {
      retryAfterTimeout = false;
      try {
        const response = onProgress
          ? await API.requestWithProgress<T>(requestConfig, onProgress)
          : await axios.request(requestConfig);

        if (response.status < 400) {
          if (onProgress) {
            return cleanJSONofNull(response.data) as T;
          }
          const processResponse = {
            json: () => {
              return cleanJSONofNull(response.data) as T;
            },
            blob: () => {
              const data = response.data;
              try {
                let filename: string;
                const contentDisposition = response.headers["content-disposition"];
                if (contentDisposition) {
                  if (contentDisposition.indexOf(`filename="`) !== -1) {
                    filename = contentDisposition.match('filename="([^"]*)"')[1];
                  } else {
                    const encodedFilename = contentDisposition.match(/filename\*=UTF-8''([^]*)/)[1];
                    filename = decodeURI(encodedFilename);
                  }
                } else {
                  filename = "undefined";
                }
                const mimeType = response.headers["content-type"].split(";")[0];
                return new File([data], filename, { type: mimeType });
              } catch (err) {
                return data;
              }
            },
            text: () => response.data,
          };

          const contentType = response.headers["content-type"] || response.headers["Content-Type"];
          const responseMimeType = contentType ? contentType.split(";")[0] : "";
          if (responseMimeType.indexOf("json") !== -1) {
            return processResponse.json() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
          } else if (responseMimeType.startsWith("text") || response.status === 204) {
            return processResponse.text();
          } else {
            return processResponse.blob();
          }
        } else {
          throw response;
        }
      } catch (err) {
        // avoid showing abort errors
        if (Axios.isCancel(err)) {
          console.warn("ABORTED: ", url);
          throw ABORTED;
        }
        const error: AxiosError = err;
        const response = error.response;

        /**
         * avoid showing 500 status with 501 code
         * https://pmgredesign.atlassian.net/browse/DEV-4811
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (response && response.status === 500 && (response.data as any).code === 501) {
          throw new Error("501 code");
        }
        if (response && response.status < 500) {
          switch (error.response.status) {
            case 401:
              // // if JWT Token needs to be refreshed
              // if (userInfoStore.isAuthenticated && userInfoStore.jwtToken) {
              //   console.log("urlReturned401Counter", urlReturned401Counter);
              // eslint-disable-next-line max-len
              //   // in case of second rejected 401 (at POST api/login that is made to refresh session) - finally logout
              //   if (urlReturned401Counter > 1) {
              //     API.handle401(JSON.stringify(error));
              //     urlReturned401Counter = 0;
              //     throw error;
              //   } else {
              //     // caching try to restore auth session in order to omit excess requests
              //     urlReturned401Counter++;
              //     if (userSettingsStore.state !== "LOADING") {
              //       await userSettingsStore.fetch();
              //     }
              //   }
              // } else
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if (response.data && (response.data as any).code === 4014) {
                // DEV-4302 - SSO refresh login
                userInfoStore.authenticate({ status: null });
                const { oauth2request } = getUrlParams(window.location.search) as {
                  oauth2request?: string;
                };
                const redirectUrl = await ssoLoginStore.fetchRedirectUrl(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (response.data as any).message,
                  "serverId",
                  oauth2request
                );
                window.open(redirectUrl, "_self");
              } else if (userInfoStore.isAuthenticated) {
                API.handle401(JSON.stringify(response), true);
              }
              throw error;
            case 408:
              retryAfterTimeout = true;
              continue;
            default: {
              const title = response.status.toString();
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const responseData = response.data as any;
              if (responseData && responseData.code) {
                if ((responseData.code === 1 || responseData.code === 1014) && responseData.message) {
                  notificationError(title, responseData.message);
                } else if (responseData.code === 3 && responseData.message) {
                  notificationError(title, responseData.message, true);
                }
              } else {
                notificationError(title, response.statusText);
              }
              throw error;
            }
          }
        } else if (err !== UNDEFINED_IN_URL) {
          if (!silent && !isUndefined) {
            const title =
              error.response && error.response.status
                ? error.response.status.toString()
                : i18n.t("Error", { ns: I18N_NS.GENERAL });
            let message: string;
            if (err.response) {
              if (err.response.data && err.response.data.message) {
                message = err.response.data.message;
              } else if (error.response.status === 504) {
                message = err.response.statusText;
              } else {
                message = i18n.t("Server is temporarily not Available", {
                  ns: I18N_NS.GENERAL,
                });
              }
            } else {
              message = i18n.t("Server did not respond", {
                ns: I18N_NS.GENERAL,
              });
            }
            notificationError(title, message);
          }
          throw new FetchError(error);
        }
        throw err;
      }
    } while (retryAfterTimeout);
    throw new Error("Unexpected API request flow");
  }

  private static async requestWithProgress<T>(
    requestConfig: AxiosRequestConfig,
    onProgress: (percent: number) => void
  ) {
    requestConfig.responseType = "stream";
    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers["Long-Runner"] = "true";
    // Use fetch adapter to access ReadableStream
    requestConfig.adapter = "fetch";

    const response = await axios.request(requestConfig);

    const reader = response.data.getReader();

    const decoder = new TextDecoder("utf-8");
    let buf = "";
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      buf += decoder.decode(value, { stream: true });
      if (done) break;

      const parts = buf.split("\n");
      if (parts.length > 2) {
        const percent = Number.parseInt(parts[parts.length - 2]);
        if (!isNaN(percent)) {
          onProgress(percent);
        }
      }
    }
    const i = buf.indexOf("\n\n");
    if (i == -1) {
      throw new Error("final data not properly speperated by double newline");
    } else {
      buf = buf.substring(i + 2);
    }

    // LongRunner always finishes with JSON (also see PAVE-4516 / PAVE-5603)
    response.data = JSON.parse(buf) as T;

    // Align headers object with axios behavior (cause of the fetch adapter)
    response.headers = response.config.headers;

    return response;
  }

  public static headersObject(additionalHeaders: unknown = {}) {
    return {
      ...(additionalHeaders as {}),
      Authorization: (additionalHeaders as { Authorization?: string }).Authorization || userInfoStore.jwtToken,
    };
  }

  public static createHeaders() {
    const httpHeader = new Headers();
    httpHeader.append("Authorization", userInfoStore.jwtToken);
    return httpHeader;
  }

  // tslint:disable-next-line: max-line-length
  // private static createRequest(url: string, { method, type, ...opts }: CreateRequestOpts): Request {
  //   if (method === 'GET') {
  //     opts.body = undefined;
  //   }

  //   let httpHeader = this.createHeaders();

  //   let init: RequestInit;
  //   init = {
  //     method: method,
  //     headers: httpHeader,
  //     credentials: 'same-origin',
  //   };

  //   if (opts.signal) {
  //     init.signal = opts.signal;
  //   }

  //   if (opts.body) {
  //     if (isObservableObject(opts.body)) {
  //       opts.body = toJS(opts.body);
  //     }
  //     if (isObservableArray(opts.body)) {
  //       opts.body = opts.body.peek();
  //     }
  //     if (type === 'json') {
  //       opts.body = JSON.stringify(opts.body);
  //       httpHeader.append('content-type', 'application/json');
  //     }
  //     if (type === 'text') {
  //       opts.body = opts.body.toString();
  //       httpHeader.append('content-type', 'text/plain');
  //     }

  //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //     init.body = opts.body as any;
  //   }

  //   return new Request(url, init);
  // }

  /**
   *
   * @param why - string to debug where sign out was called. Logged as console.warn.
   * @param silent - boolean that stands for sending DELETE request to close session.
   */
  private static async handle401(why: string, silent?: boolean) {
    const userRole = userInfoStore.role || "USER";
    await userInfoStore.signOut(why, silent);
    let redirectURl = "/login";
    if (userRole === "BIDDER" || userRole === "GUEST") {
      redirectURl += "/external";
    }
    RouterStore.push(redirectURl, { from: RouterStore.location });
  }
}

/**
 * Helper function to ensure that all null values are removed from the server Response
 * This is needed, as currently the Server may or may not send a value as null;
 *
 * @param obj JSON Structure
 */
function cleanJSONofNull(obj: Object) {
  Object.keys(obj).forEach((k) => !obj[k] && obj[k] === null && delete obj[k]);
  return obj;
}
